import { useEffect, useState } from 'react';
import { Linking, Platform } from 'react-native';

interface PackageInfo {
	type: string;
	vip?: {
		durationDays: number;
	};
	chips?: number;
	id?: string;
	name?: string;
	price?: number;
	currency?: Array<any>;
	chipAmount?: number;
}

interface PaymentStatusData {
	status: 'success' | 'cancelled' | null;
	sessionId?: string;
	depositId?: string;
	packageInfo?: PackageInfo;
}

function parsePaymentUrl(url: string | null): PaymentStatusData | null {
	if (!url) return null;

	// URL в RN може да е: myapp://payment?payment=success&deposit_id=123
	// или https://domain.com/payment?...
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		// ако URL() гръмне (рядко), няма как да парснем
		return null;
	}

	const params = parsed.searchParams;
	const payment = params.get('payment');
	const sessionId = params.get('session_id') || undefined;
	const depositId = params.get('deposit_id') || undefined;

	if (payment !== 'success' && payment !== 'cancelled') return null;

	const data: PaymentStatusData = {
		status: payment,
		sessionId,
		depositId,
	};

	if (payment === 'success' && depositId) {
		try {
			// Use localStorage directly since this is a synchronous function
			const saved =
				typeof localStorage !== 'undefined' ? localStorage.getItem(`package_${depositId}`) : null;
			if (saved) {
				data.packageInfo = JSON.parse(saved);
				if (typeof localStorage !== 'undefined') {
					localStorage.removeItem(`package_${depositId}`);
				}
			}
		} catch (e) {
			console.error('Failed to parse package info:', e);
		}
	}

	return data;
}

export function usePaymentStatus(): PaymentStatusData {
	const [paymentData, setPaymentData] = useState<PaymentStatusData>({
		status: null,
	});

	useEffect(() => {
		let sub: any;

		const setFromUrl = (url: string | null) => {
			const parsed = parsePaymentUrl(url);
			if (parsed) setPaymentData(parsed);
		};

		if (Platform.OS === 'web') {
			if (typeof window === 'undefined') return;

			const url = window.location.href;
			setFromUrl(url);

			const parsed = parsePaymentUrl(url);
			if (parsed) {
				const noQueryUrl = window.location.pathname;
				window.history.replaceState({}, '', noQueryUrl);
			}

			// The checkout runs in a popup tab on web. When it completes, that tab stores
			// `web_payment_result` and closes itself; pick the result up here without
			// reloading so the player stays exactly where they are (e.g. at the table).
			const handleStorage = (e: StorageEvent) => {
				if (e.key !== 'web_payment_result') return;

				try {
					const raw = window.localStorage.getItem('web_payment_result');
					if (!raw) return;
					window.localStorage.removeItem('web_payment_result');

					const result = JSON.parse(raw);
					const status: PaymentStatusData['status'] = result?.status;

					if (status !== 'success' && status !== 'cancelled') return;

					const depositId: string = String(result?.depositId || '');
					let packageInfo: PackageInfo | undefined;

					if (status === 'success' && depositId) {
						const saved = window.localStorage.getItem(`package_${depositId}`);
						if (saved) {
							packageInfo = JSON.parse(saved);
							window.localStorage.removeItem(`package_${depositId}`);
						}
					}

					setPaymentData({
						status,
						depositId,
						sessionId: result?.sessionId || undefined,
						packageInfo,
					});
				} catch (err) {
					console.error('Failed to handle web payment result:', err);
				}
			};

			window.addEventListener('storage', handleStorage);
			return () => window.removeEventListener('storage', handleStorage);
		}

		Linking.getInitialURL()
			.then(setFromUrl)
			.catch(() => {});

		sub = Linking.addEventListener('url', e => setFromUrl(e.url));

		return () => {
			sub?.remove?.();
		};
	}, []);

	return paymentData;
}
